import React, { useEffect, useMemo, useRef, useState } from 'react';

interface ReasoningPanelProps {
  reasoningText: string;
  streaming: boolean;
  initiallyOpen?: boolean;
  maxHeight?: number;
  titleWhenStreaming?: string;
  titleWhenDone?: string;
}

const ReasoningPanel: React.FC<ReasoningPanelProps> = ({
  reasoningText,
  streaming,
  initiallyOpen = false,
  maxHeight = 240,
  titleWhenStreaming = 'Thinking…',
  titleWhenDone = 'Thinking (completed)'
}) => {
  const [show, setShow] = useState<boolean>(initiallyOpen);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [stickToBottom, setStickToBottom] = useState<boolean>(true);

  // While streaming, force open; after streaming, use toggle state
  const isOpen = useMemo(() => streaming || show, [streaming, show]);

  useEffect(() => {
    if (!scrollRef.current) return;
    if (!reasoningText) return;
    if (stickToBottom) {
      const el = scrollRef.current;
      el.scrollTop = el.scrollHeight;
    }
  }, [reasoningText, stickToBottom]);

  // Debug: Log when reasoningText length decreases for any reason
  const previousReasoningTextLengthRef = useRef<number>(0);
  useEffect(() => {
    const previousLength = previousReasoningTextLengthRef.current;
    const currentLength = reasoningText ? reasoningText.length : 0;
    if (previousLength > 0 && currentLength < previousLength) {
      console.log('[ReasoningPanel] reasoningText shortened', {
        previousLength,
        currentLength,
        delta: currentLength - previousLength,
        streaming,
        timestamp: new Date().toISOString()
      });
    }
    previousReasoningTextLengthRef.current = currentLength;
  }, [reasoningText, streaming]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
    setStickToBottom(atBottom);
  };

  if (!streaming && !reasoningText) return null;

  return (
    <div style={{ marginBottom: 12, padding: 8, background: '#f6f6f6', border: '1px solid #e0e0e0', borderRadius: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
        <div style={{ fontSize: 12, opacity: 0.8 }}>
          {streaming ? titleWhenStreaming : titleWhenDone}
        </div>
        <button
          type="button"
          onClick={() => setShow(v => !v)}
          aria-label={isOpen ? 'Hide thinking' : 'Show thinking'}
          title={isOpen ? 'Hide thinking' : 'Show thinking'}
          style={{ background: 'transparent', border: 'none', padding: '2px 6px', fontSize: 12, color: '#666', cursor: 'pointer', borderRadius: 4, opacity: 0.9 }}
        >
          🧠 {isOpen ? 'Hide' : 'Show'}
        </button>
      </div>
      {isOpen && (
        <div ref={scrollRef} onScroll={handleScroll} style={{ maxHeight, overflowY: 'auto' }}>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, lineHeight: 1.4 }}>{reasoningText}</pre>
        </div>
      )}
    </div>
  );
};

export default ReasoningPanel;


